<template>
  <v-dialog
    v-model="isDialogOpen"
    @keydown.escape="closeDialog"
    @after-leave="resetDialogProps"
    transition="slide-left"
    content-class="drawer-like"
    v-bind="$attrs">
    <v-card data-testid="edit-task-dialog">
      <v-card-title class="d-flex flex-row align-center">
        <h2 class="typography-heading-lg">{{ t('global.basicTerms.tasks') }} #{{ taskNumber }}</h2>
        <v-spacer></v-spacer>

        <yard-task-actions-menu
          :task="viewTask"
          :is-loading="isLoading"
          :button-props="{ density: 'default' }" />
        <v-btn variant="text" icon="mdi-close" @click="closeDialog" />
      </v-card-title>

      <template v-if="isLoading">
        <div class="d-flex justify-center align-center h-100 px-3">
          <v-progress-linear indeterminate color="primary"></v-progress-linear>
        </div>
      </template>
      <template v-else>
        <v-card-text
          v-if="!isLoading && currentTask"
          class="d-flex flex-column gap-4"
          style="padding: 0 !important">
          <div class="yard-task-header">
            <div
              class="yard-task-header__status"
              :class="`yard-task-header__status--${taskStatus}`">
              <v-icon
                :icon="taskStatusIcon"
                size="16"
                class="yard-task-header__status__icon"></v-icon>
              <span class="typography-caption-bold yard-task-header__status__label">
                {{ taskStatusLabel }}
              </span>
            </div>

            <div class="yard-task-header__status-by">
              <span class="typography-caption-bold yard-task-header__status-by__label">
                {{ taskStatusBy }}
              </span>
              <span class="typography-caption yard-task-header__status-by__value">
                {{ taskStatusByValue }}
              </span>
            </div>

            <div class="yard-task-header__status-at">
              <span class="typography-caption-bold yard-task-header__status-at__label">
                {{ taskStatusAt }}
              </span>
              <span class="typography-caption yard-task-header__status-at__value">
                {{ taskStatusAtValue }}
              </span>
              <elapsed-time
                v-if="inProgressEvent && completedEvent"
                class="yard-task-header__status-at__elapsed-time"
                variant="human"
                :from="inProgressEvent.rawAt"
                :until="completedEvent.rawAt"
                :timezone="warehouseTimezone"
                :update-interval="0" />
            </div>
          </div>

          <div class="d-flex flex-column gap-4 flex-grow-1">
            <v-tabs v-model="activeTab" color="primary" class="custom-tabs mx-6 my-0">
              <v-tab value="task">{{ t('yard.drawers.editTaskDrawer.tabs.task') }}</v-tab>
              <v-tab value="activity">
                {{ t('yard.drawers.editTaskDrawer.tabs.commentsAndActivity') }}
              </v-tab>
            </v-tabs>

            <v-tabs-window v-model="activeTab" class="custom-tabs-window">
              <v-tabs-window-item value="task">
                <div class="pt-0 px-6 pb-4">
                  <component
                    :is="getTaskFormComponent()"
                    v-model="currentTask"
                    :disabledFields="disabledFieldsConfig"
                    @validate="handleValidate" />
                </div>
              </v-tabs-window-item>
              <v-tabs-window-item value="activity" class="flex-grow-1">
                <div class="d-flex flex-column h-100">
                  <div class="pt-0 px-6 pb-4 mt-auto">
                    <yard-task-timeline :task-id="taskId" :warehouse-timezone="warehouseTimezone" />
                  </div>
                  <v-divider></v-divider>
                  <div class="px-6 py-4">
                    <yard-task-comment-form
                      autofocus
                      related-entity="YardTask"
                      :related-id="taskId"
                      @comment="handleNewComment" />
                  </div>
                </div>
              </v-tabs-window-item>
            </v-tabs-window>
          </div>
        </v-card-text>
        <template v-if="activeTab === 'task'">
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-btn
              variant="text"
              @click="closeDialog"
              data-testid="edit-task-dialog-action-nevermind">
              {{ t('global.buttons.nevermind') }}
            </v-btn>
            <primary-button
              :disabled="isSubmitDisabled"
              @click="updateTask"
              data-testid="edit-task-dialog-action-save">
              {{ t('global.buttons.save') }}
            </primary-button>
          </v-card-actions>
        </template>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isEmpty, cloneDeep } from 'lodash';

import { useDialogListener, useNotify } from '@/composables';
import { useYardTaskMutations } from '@satellite/composables/useYardTaskMutations';
import ElapsedTime from '@satellite/components/elements/ElapsedTime.vue';
import YardTaskCommentForm from '@satellite/components/YardTaskTimeline/YardTaskCommentForm.vue';
import YardTaskTimeline from '@satellite/components/YardTaskTimeline/YardTaskTimeline.vue';

import { createTaskDisabledFields, hasTaskChanged } from './EventTriggeredEditTaskDrawer.helpers';
import { transformTaskViewDataToFormData } from '@satellite/helpers/yardTaskUtils';
import { useYardTask } from './useYardTask';
import YardTaskActionsMenu from './YardTaskActionsMenu.vue';
import YardTasksCreateCheckTaskForm from './YardTasksCreateCheckTaskForm.vue';
import YardTasksCreateMoveTaskForm from './YardTasksCreateMoveTaskForm.vue';

export default defineComponent({
  name: 'EventTriggeredEditTaskDrawer',
  components: {
    ElapsedTime,
    YardTasksCreateMoveTaskForm,
    YardTasksCreateCheckTaskForm,
    YardTaskTimeline,
    YardTaskCommentForm,
    YardTaskActionsMenu
  },
  setup() {
    const { t } = useI18n();
    const notify = useNotify();
    const {
      isDialogOpen,
      dialogProps,
      closeDialog,
      resetDialogProps,
      subscribeToDialogEvent,
      unsubscribeFromDialogEvent
    } = useDialogListener('open-edit-yard-task-drawer');

    onMounted(() => {
      subscribeToDialogEvent();
    });

    onUnmounted(() => {
      unsubscribeFromDialogEvent();
    });

    const activeTab = ref('task');
    const isSubmitDisabled = ref(true);

    const taskId = computed(() => dialogProps.value?.taskId);

    /**
     * TODO: (welington) Add live option to useYardTask to refresh the task when the
     * task is updated. Ideally, we should offer the user the ability to refresh the task manually.
     */
    const {
      cancelledEvent,
      completedEvent,
      createdAt: taskCreatedAt,
      createdBy,
      inProgressEvent,
      isLoading,
      isTaskCancelled,
      isTaskCompleted,
      isTaskInProgress,
      statusIcon: taskStatusIcon,
      statusLabel: taskStatusLabel,
      task: viewTask,
      taskNumber,
      taskStatus,
      taskType,
      warehouseTimezone
    } = useYardTask({ taskId });

    const transformedTask = computed(() => {
      if (!viewTask.value) {
        return null;
      }

      return transformTaskViewDataToFormData(viewTask.value, warehouseTimezone.value);
    });

    const currentTask = ref(null);

    // Event computed properties (now directly from presenter)
    const taskInProgressBy = computed(() => inProgressEvent.value?.by || null);
    const taskCompletedBy = computed(() => completedEvent.value?.by || null);
    const taskCancelledBy = computed(() => cancelledEvent.value?.by || null);
    const taskInProgressAt = computed(() => inProgressEvent.value?.at || null);
    const taskCompletedAt = computed(() => completedEvent.value?.at || null);
    const taskCancelledAt = computed(() => cancelledEvent.value?.at || null);

    const disabledFieldsConfig = computed(() => {
      if (!taskStatus.value || !taskType.value) {
        return {};
      }

      const context = {
        taskStatus: taskStatus.value,
        taskType: taskType.value
      };

      return createTaskDisabledFields(context);
    });

    const taskStatusBy = computed(() => {
      if (isTaskInProgress.value) {
        return t('yard.drawers.editTaskDrawer.labels.inProgressBy');
      } else if (isTaskCompleted.value) {
        return t('yard.drawers.editTaskDrawer.labels.completedBy');
      } else if (isTaskCancelled.value) {
        return t('yard.drawers.editTaskDrawer.labels.cancelledBy');
      }

      return t('yard.drawers.editTaskDrawer.labels.createdBy');
    });

    const taskStatusByValue = computed(() => {
      if (isTaskInProgress.value) {
        return taskInProgressBy.value;
      } else if (isTaskCompleted.value) {
        return taskCompletedBy.value;
      } else if (isTaskCancelled.value) {
        return taskCancelledBy.value;
      }

      return createdBy.value;
    });

    const taskStatusAt = computed(() => {
      if (isTaskInProgress.value) {
        return t('yard.drawers.editTaskDrawer.labels.startedAt');
      } else if (isTaskCompleted.value) {
        return t('yard.drawers.editTaskDrawer.labels.completedAt');
      } else if (isTaskCancelled.value) {
        return t('yard.drawers.editTaskDrawer.labels.cancelledAt');
      }

      return t('yard.drawers.editTaskDrawer.labels.createdAt');
    });

    const taskStatusAtValue = computed(() => {
      if (isTaskInProgress.value) {
        return taskInProgressAt.value;
      } else if (isTaskCompleted.value) {
        return taskCompletedAt.value;
      } else if (isTaskCancelled.value) {
        return taskCancelledAt.value;
      }

      return taskCreatedAt.value;
    });

    watch(transformedTask, newTransformedTask => {
      if (newTransformedTask) {
        // Clone to avoid mutating the reference and compromising change detection
        currentTask.value = cloneDeep(newTransformedTask);
        isSubmitDisabled.value = true;
      } else {
        currentTask.value = null;
        isSubmitDisabled.value = true;
      }
    });

    const getTaskFormComponent = () => {
      if (taskType.value === 'move') {
        return YardTasksCreateMoveTaskForm;
      }

      if (taskType.value === 'check') {
        return YardTasksCreateCheckTaskForm;
      }

      return null;
    };

    const handleValidate = isValid => {
      isSubmitDisabled.value =
        isEmpty(transformedTask.value) ||
        !hasTaskChanged(currentTask.value, transformedTask.value) ||
        !isValid;
    };

    const { updateTask: updateTaskMutation } = useYardTaskMutations({
      warehouseTimezone
    });

    const updateTask = async () => {
      if (!dialogProps.value) {
        console.error('Failed to update task: No dialog props found');
        return;
      }

      if (!taskId.value) {
        console.error('Failed to update task: No task ID found');
        return;
      }

      if (!currentTask.value) {
        console.error('Failed to update task: No task data available');
        return;
      }

      let task = null;
      try {
        task = await updateTaskMutation(taskId.value, currentTask.value);

        notify(t(`yard.messages.${taskType.value}TaskUpdated`), 'success');
      } catch (error) {
        console.error('Failed to update yard task:', error);
        notify(t('yard.messages.failedToUpdateYardTask'), 'error');
        return;
      }

      try {
        dialogProps.value.onTaskUpdated?.(task);
      } catch (error) {
        console.error('Error in post-update callback:', error);
      }

      closeDialog();
    };

    const scrollToBottom = () => {
      const timelineElement = document.querySelector('[data-testid="edit-task-dialog"]');

      if (timelineElement) {
        setTimeout(() => {
          timelineElement.scrollTo({
            top: timelineElement.scrollHeight,
            behavior: 'smooth'
          });
        }, 250);
      }
    };

    const handleNewComment = () => {
      scrollToBottom();
    };

    watch(isDialogOpen, newIsOpen => {
      if (!newIsOpen) {
        activeTab.value = 'task';
      }
    });

    watch(activeTab, newActiveTab => {
      if (newActiveTab === 'activity') {
        scrollToBottom();
      }
    });

    return {
      dialogProps,
      activeTab,
      isLoading,
      taskId,
      currentTask,
      viewTask,
      isDialogOpen,
      isSubmitDisabled,
      taskNumber,
      taskStatus,
      taskStatusIcon,
      taskStatusLabel,
      isTaskCompleted,
      inProgressEvent,
      completedEvent,
      taskStatusBy,
      taskStatusAt,
      taskStatusByValue,
      taskStatusAtValue,
      disabledFieldsConfig,
      warehouseTimezone,
      t,
      closeDialog,
      resetDialogProps,
      getTaskFormComponent,
      handleValidate,
      updateTask,
      handleNewComment
    };
  }
});
</script>

<style scoped lang="scss">
// TODO: (welington) extract the drawer-like styles to a shared component.
:deep(.drawer-like) {
  width: 620px;
  max-width: 100%;
  position: fixed;
  right: 0;
  margin: 0;
  height: 100vh;
  max-height: 100%;

  .v-card {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
  }
}

:deep(.custom-tabs-window) {
  flex-grow: 1;

  .v-window__container {
    height: 100%;
  }
}

.yard-task-header {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  border-radius: 4px;
  border: 1px solid $color-line-border;
  background-color: $color-neutral-10;

  margin: $spacing-4 $spacing-6 0;
}

.yard-task-header__status {
  flex: 1;

  --yard-task-header-status-background-color: #{$color-neutral-10};
  --yard-task-header-status-icon-color: #{$color-neutral-80};
  --yard-task-header-status-label-color: #{$color-neutral-90};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing-0-5;
  padding: $spacing-2 $spacing-3;

  border-radius: 4px 0 0 4px;

  background-color: var(--yard-task-header-status-background-color);
}

.yard-task-header__status__icon {
  color: var(--yard-task-header-status-icon-color);
}

.yard-task-header__status__label {
  color: var(--yard-task-header-status-label-color);
}

.yard-task-header__status {
  &--in-progress {
    --yard-task-header-status-background-color: #{$color-primary-20};
    --yard-task-header-status-icon-color: #{$color-primary-100};
  }

  &--completed {
    --yard-task-header-status-background-color: #{$color-success-20};
    --yard-task-header-status-icon-color: #{$color-success-60};
  }

  &--cancelled {
    --yard-task-header-status-background-color: #{$color-error-20};
    --yard-task-header-status-icon-color: #{$color-error-60};
  }
}

.yard-task-header__status-by {
  flex: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing-0-5;
  padding: $spacing-2 $spacing-3;

  border-left: 1px solid $color-line-border;
}

.yard-task-header__status-at {
  flex: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing-0-5;
  padding: $spacing-2 $spacing-3;

  border-left: 1px solid $color-line-border;
}

.yard-task-header__status-by__label,
.yard-task-header__status-at__label {
  color: $color-text-secondary;
}

.yard-task-header__status-by__value,
.yard-task-header__status-at__value {
  color: $color-text-primary;
}

.yard-task-header__status-at__elapsed-time {
  text-align: center;
  font-family: Barlow;
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 14px */
}
</style>
